import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface SplitPaymentData {
  id: string;
  event_id: string;
  payer_user_id: string;
  partner_user_id: string;
  payer_amount: number;
  partner_amount: number;
  partner_payment_status: string;
  payer_payment_intent_id: string | null;
  payer_checkout_session_id: string | null;
  status: string;
  expires_at: string;
  created_at: string;
  registration_data?: any;
  payer_profile?: { full_name: string | null; avatar_url: string | null };
  partner_profile?: { full_name: string | null; avatar_url: string | null };
  /** Whether the current user is the payer ('payer') or the partner ('partner') */
  userRole?: 'payer' | 'partner';
}

export const useSplitPaymentStatus = (eventId: string | undefined) => {
  const { user } = useAuth();
  const [splitPayment, setSplitPayment] = useState<SplitPaymentData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSplitPayment = useCallback(async () => {
    if (!user || !eventId) {
      setLoading(false);
      return;
    }

    try {
      // Query split payments where current user is either payer or partner
      // Include active states only, ordered by most recent
      const { data, error } = await supabase
        .from('split_payments')
        .select('*')
        .eq('event_id', eventId)
        .in('status', ['pending_partner', 'pending_payer', 'active'])
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error fetching split payment:', error);
        setLoading(false);
        return;
      }

      // RLS already filters to rows where user is payer or partner
      const record = data && data.length > 0 ? data[0] : null;

      if (!record) {
        setSplitPayment(null);
        setLoading(false);
        return;
      }

      // Determine user role
      const userRole: 'payer' | 'partner' =
        record.payer_user_id === user.id ? 'payer' : 'partner';

      // Fetch profiles for payer and partner
      const [payerRes, partnerRes] = await Promise.all([
        supabase.from('profiles').select('full_name, avatar_url').eq('user_id', record.payer_user_id).maybeSingle(),
        supabase.from('profiles').select('full_name, avatar_url').eq('user_id', record.partner_user_id).maybeSingle(),
      ]);

      setSplitPayment({
        ...record,
        userRole,
        payer_profile: payerRes.data || { full_name: null, avatar_url: null },
        partner_profile: partnerRes.data || { full_name: null, avatar_url: null },
      });
    } catch (err) {
      console.error('Error in useSplitPaymentStatus:', err);
    } finally {
      setLoading(false);
    }
  }, [user, eventId]);

  useEffect(() => {
    fetchSplitPayment();
  }, [fetchSplitPayment]);

  // Realtime subscription + polling fallback
  useEffect(() => {
    if (!user || !eventId) return;

    const channel = supabase
      .channel(`split-payment-${eventId}-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'split_payments',
          filter: `event_id=eq.${eventId}`,
        },
        () => {
          fetchSplitPayment();
        }
      )
      .subscribe();

    // Polling every 15s as fallback when realtime misses UPDATE events
    // (requires REPLICA IDENTITY FULL on the table for realtime to work fully)
    const pollInterval = setInterval(fetchSplitPayment, 15000);

    // Refetch when user returns to the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchSplitPayment();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, eventId, fetchSplitPayment]);

  const clear = useCallback(() => setSplitPayment(null), []);
  return { splitPayment, loading, refetch: fetchSplitPayment, clear };
};
